// app/components/product-modal/product-bundles.tsx
// Component to display multi-unit pricing (MUP) bundle deals

import { useState } from 'react';
import { useStore } from '~/context/store-context';
import { useCart } from '~/context/cart-context';
import { executeCartOperation } from '~/utils/cart-helper';
import { toast } from 'react-hot-toast';

interface BundleProduct {
  id: string;
  name: string;
  image: string;
  price: number;
  treez_id: string;
  inStock: boolean;
}

interface ProductBundlesProps {
  currentProductId: string;
  currentProductPrice: number; // Current discounted price (e.g., $16.10)
  originalPrice?: number; // Original price before discounts (e.g., $23.00)
  budflowMup?: {
    active: boolean;
    products: string[];
    rule_config: {
      quantity?: number; // For buy_x_get_for_y, buy_x_get_percent_off, buy_x_items_get_amount_off
      target_price?: number; // For buy_x_get_for_y
      discount_percent?: number; // For buy_x_get_percent_off
      discount_amount?: number; // For buy_x_items_get_amount_off, spend_x_get_y_off
      minimum_spend?: number; // For spend_x_get_y_off
    };
    rule_id: string;
    rule_name: string;
    rule_type: string; // 'buy_x_get_for_y' | 'buy_x_get_percent_off' | 'buy_x_items_get_amount_off' | 'spend_x_get_y_off'
  };
  onBundleProductClick?: (productId: string) => void;
}

export default function ProductBundles({
  currentProductId,
  currentProductPrice,
  originalPrice,
  budflowMup,
  onBundleProductClick,
}: ProductBundlesProps) {
  const [bundleProducts, setBundleProducts] = useState<BundleProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedProducts, setHasLoadedProducts] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [addedProducts, setAddedProducts] = useState<Set<string>>(new Set());
  const { selectedStore } = useStore();
  const { incrementCount, openCart } = useCart();

  // Check if MUP is active and has products
  const hasMup = budflowMup?.active && budflowMup.products && budflowMup.products.length > 1;

  // Determine rule type
  const isPercentOff = budflowMup?.rule_type === 'buy_x_get_percent_off';
  const isFixedPrice = budflowMup?.rule_type === 'buy_x_get_for_y';
  const isAmountOff = budflowMup?.rule_type === 'buy_x_items_get_amount_off';
  const isSpendBased = budflowMup?.rule_type === 'spend_x_get_y_off';

  // Calculate bundle savings based on rule type
  // BEST DEAL WINS: Calculate MUP based on ORIGINAL price, compare to current discounted price
  const calculateSavings = () => {
    if (!budflowMup || !hasMup) return { savings: 0, savingsPercent: 0, regularTotal: 0, bundlePrice: 0, isSpendBased: false, isBetterDeal: false };

    const quantity = budflowMup.rule_config.quantity || 1;
    // Use original price for MUP calculation, fall back to current price if not provided
    const priceForMupCalc = originalPrice || currentProductPrice;
    const originalTotal = priceForMupCalc * quantity;
    const currentDiscountedTotal = currentProductPrice * quantity;

    let mupBundlePrice = originalTotal;
    let displaySavingsPercent = 0;

    if (isPercentOff && budflowMup.rule_config.discount_percent) {
      // Percent off: calculate discount from ORIGINAL price
      const discountPercent = budflowMup.rule_config.discount_percent;
      mupBundlePrice = originalTotal * (1 - discountPercent / 100);
      displaySavingsPercent = discountPercent;
    } else if (isFixedPrice && budflowMup.rule_config.target_price) {
      // Fixed price: use target price directly
      mupBundlePrice = budflowMup.rule_config.target_price;
      displaySavingsPercent = ((originalTotal - mupBundlePrice) / originalTotal) * 100;
    } else if (isAmountOff && budflowMup.rule_config.discount_amount) {
      // Amount off: flat discount from original
      mupBundlePrice = originalTotal - budflowMup.rule_config.discount_amount;
      displaySavingsPercent = (budflowMup.rule_config.discount_amount / originalTotal) * 100;
    } else if (isSpendBased && budflowMup.rule_config.minimum_spend && budflowMup.rule_config.discount_amount) {
      // Spend-based: spend X get Y off
      const minimumSpend = budflowMup.rule_config.minimum_spend;
      const discountAmount = budflowMup.rule_config.discount_amount;
      displaySavingsPercent = (discountAmount / minimumSpend) * 100;

      return {
        savings: discountAmount,
        savingsPercent: Math.max(0, displaySavingsPercent),
        regularTotal: originalTotal,
        bundlePrice: minimumSpend - discountAmount,
        isSpendBased: true,
        minimumSpend,
        isBetterDeal: true, // Spend-based always shown as it's additive
      };
    }

    // BEST DEAL WINS: Compare MUP price to current discounted price
    // MUP is better if bundle price is less than or equal to current discounted total
    const isBetterDeal = mupBundlePrice <= currentDiscountedTotal;
    
    // Calculate actual savings from original price (for display)
    const savingsFromOriginal = originalTotal - mupBundlePrice;

    return {
      savings: Math.max(0, savingsFromOriginal),
      savingsPercent: Math.max(0, displaySavingsPercent),
      regularTotal: originalTotal,
      bundlePrice: mupBundlePrice,
      isSpendBased: false,
      isBetterDeal,
    };
  };

  // Fetch bundle products when expanded
  const fetchBundleProducts = async () => {
    if (!budflowMup || !budflowMup.products.length || hasLoadedProducts) return;

    setIsLoading(true);
    setHasLoadedProducts(true);

    try {
      const productPromises = budflowMup.products.map(async (treezId) => {
        try {
          const response = await fetch(
            `/api/product_by_treez_id?treez_id=${treezId}&store=${selectedStore}`,
          );
          if (!response.ok) return null;
          const data = await response.json();

          if (!data.found) return null;

          return {
            id: data.product.id,
            name: data.product.name,
            image: data.product.primary_image || data.product.image,
            price: data.product.price,
            treez_id: treezId,
            inStock: data.inStock,
          };
        } catch (error) {
          console.error(`Error fetching bundle product ${treezId}:`, error);
          return null;
        }
      });

      const results = await Promise.all(productPromises);
      const validProducts = results.filter((p): p is BundleProduct => p !== null && p.inStock);
      setBundleProducts(validProducts);
    } catch (error) {
      console.error('Error fetching bundle products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add to cart for a bundle product
  const handleAddToCart = async (product: BundleProduct, e: React.MouseEvent) => {
    e.stopPropagation();
    setAddingToCart(product.id);

    try {
      const storeKey = localStorage.getItem('selectedStore');
      const order_id = storeKey
        ? localStorage.getItem(`token_${storeKey}`) || 'default_order_id'
        : 'default_order_id';

      const payload = {
        actionType: 'ADD' as const,
        order_id,
        product_id: product.id,
        quantity: '1',
      };

      const { data, wasRetried } = await executeCartOperation(payload);

      if (wasRetried && storeKey) {
        const newToken = data?.addedCart?.cart?.id;
        if (newToken) {
          localStorage.setItem(`token_${storeKey}`, newToken);
        }
      }

      incrementCount(1);
      toast.success(`Added ${product.name.split(' - ')[0]} to cart`);

      // Mark this product as added
      setAddedProducts(prev => new Set(prev).add(product.id));
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  // Handle expand/collapse
  const handleToggleExpand = () => {
    if (!isExpanded && !hasLoadedProducts) {
      fetchBundleProducts();
    }
    setIsExpanded(!isExpanded);
  };

  const savingsData = calculateSavings();
  const { savings, savingsPercent, regularTotal, bundlePrice, isBetterDeal } = savingsData;

  // Don't render if no MUP, not active, or current discount is already better
  if (!hasMup || !isBetterDeal) {
    return null;
  }
  const quantity = budflowMup.rule_config.quantity || 1;
  const minimumSpend = budflowMup.rule_config.minimum_spend;
  const discountAmount = budflowMup.rule_config.discount_amount;

  // Generate display text based on rule type
  const getSubtitleText = () => {
    if (isSpendBased && minimumSpend && discountAmount) {
      return `Spend $${minimumSpend}, get $${discountAmount} off`;
    } else if (isAmountOff && discountAmount) {
      return `Buy ${quantity}, get $${discountAmount} off`;
    } else if (isPercentOff && budflowMup.rule_config.discount_percent) {
      return `Buy ${quantity}, get ${budflowMup.rule_config.discount_percent}% off`;
    } else {
      return `Buy ${quantity} for $${bundlePrice.toFixed(2)}`;
    }
  };

  const getBadgeText = () => {
    if (isSpendBased && discountAmount) {
      return `$${discountAmount} OFF`;
    } else if (isAmountOff && discountAmount) {
      return `$${discountAmount} OFF`;
    } else if (isPercentOff && budflowMup.rule_config.discount_percent) {
      return `${budflowMup.rule_config.discount_percent}% OFF`;
    } else {
      return `SAVE $${savings.toFixed(2)}`;
    }
  };

  const getInstructionText = () => {
    if (isSpendBased && minimumSpend) {
      return `Spend $${minimumSpend} on these products to get $${discountAmount} off:`;
    } else {
      return `Add ${quantity} of these products to your cart for the bundle discount:`;
    }
  };

  return (
    <div className="w-full rounded-lg border-2 border-black bg-white p-4">
      {/* Bundle Header - Always Visible */}
      <button
        onClick={handleToggleExpand}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={isExpanded}
        aria-controls="bundle-products-list"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <div>
            <p className="font-chalet text-sm font-bold text-black">
              Bundle & Save
            </p>
            <p className="text-xs text-black/70">
              {getSubtitleText()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-black px-3 py-1 text-xs font-bold text-white">
            {getBadgeText()}
          </span>
          <svg
            className={`h-5 w-5 text-black transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Expanded Bundle Products */}
      {isExpanded && (
        <div id="bundle-products-list" className="mt-4 border-t border-black/20 pt-4">
          <p className="mb-3 text-xs text-black/70">
            {getInstructionText()}
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-black/20 border-t-black" />
              <span className="ml-2 text-sm text-black/70">Loading bundle products...</span>
            </div>
          ) : bundleProducts.length > 0 ? (
            <div className="space-y-3">
              {bundleProducts.map((product) => (
                <div
                  key={product.id}
                  className={`group relative flex items-center gap-3 rounded-lg border p-3 transition-all ${
                    product.id === currentProductId
                      ? 'border-black bg-black/5'
                      : 'border-black/20 bg-white hover:border-black'
                  }`}
                >
                  {/* Product Image */}
                  <button
                    onClick={() => onBundleProductClick?.(product.id)}
                    className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md bg-gray-100"
                  >
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-200">
                        <span className="text-xl text-gray-400">🌿</span>
                      </div>
                    )}
                  </button>

                  {/* Product Info */}
                  <div className="flex flex-1 flex-col">
                    <button
                      onClick={() => onBundleProductClick?.(product.id)}
                      className="text-left"
                    >
                      <p className="font-chalet line-clamp-2 text-sm font-bold text-black">
                        {product.name.split(' - ')[0]}
                      </p>
                    </button>
                    <p className="mt-1 text-sm font-bold text-black">
                      ${product.price.toFixed(2)}
                    </p>
                  </div>

                  {/* Add to Cart Button */}
                  <div className="flex flex-col items-end gap-1">
                    <button
                      onClick={(e) => handleAddToCart(product, e)}
                      disabled={addingToCart === product.id}
                      className="font-chalet flex h-10 items-center justify-center rounded-lg border border-black bg-white px-4 text-xs font-bold text-black transition-colors hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Add ${product.name} to cart`}
                    >
                      {addingToCart === product.id ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <>
                          <svg
                            className="mr-1 h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Add
                        </>
                      )}
                    </button>
                    {addedProducts.has(product.id) && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-green-600">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Added
                      </span>
                    )}
                  </div>

                  {/* Current Product Indicator */}
                  {product.id === currentProductId && (
                    <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black text-white">
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-black/50">
              No other bundle products available
            </p>
          )}

          {/* Bundle Summary */}
          <div className="mt-4 rounded-lg bg-black/5 p-3">
            {isSpendBased ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-black/70">Minimum spend:</span>
                  <span className="text-black font-bold">${minimumSpend?.toFixed(2)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-black/10 pt-2">
                  <span className="text-sm font-bold text-black">You save:</span>
                  <span className="font-chalet rounded bg-black px-2 py-1 text-sm font-bold text-white">
                    ${discountAmount} OFF
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-black/70">Regular price ({quantity} items):</span>
                  <span className="text-black/50 line-through">${regularTotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm font-bold">
                  <span className="text-black">Bundle price:</span>
                  <span className="text-black">${bundlePrice.toFixed(2)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-black/10 pt-2">
                  <span className="text-sm font-bold text-black">You save:</span>
                  <span className="font-chalet rounded bg-black px-2 py-1 text-sm font-bold text-white">
                    {isAmountOff ? `$${discountAmount}` : `$${savings.toFixed(2)}`} ({savingsPercent.toFixed(0)}% OFF)
                  </span>
                </div>
              </>
            )}
          </div>

          {/* View Cart Button */}
          <button
            onClick={() => openCart()}
            className="font-chalet mt-4 flex h-12 w-full items-center justify-center rounded-lg bg-black text-sm font-bold text-white transition-colors hover:bg-black/80"
          >
            View Cart
          </button>
        </div>
      )}
    </div>
  );
}
